// Stale-chunk recovery. Every deploy ships a fresh container holding only the new build's hashed
// assets, so a tab opened before the deploy still references chunks (e.g. DealsListPage-<oldhash>.js)
// that now 404. The fix is a full reload: index.html is served no-cache, so the reload picks up the
// new entry and its new chunk names. Guarded by a sessionStorage timestamp so a chunk that is
// genuinely broken (not just stale) can't put the tab into a reload loop.
import { logger } from '@/lib/logger';

const STORAGE_KEY = 'quralyst:chunk-reload-at';
// A second failure inside this window means the reload didn't help — stop and show the error.
const RELOAD_GUARD_MS = 10_000;

// Chrome / Edge, Firefox, Safari, plus Vite's own CSS-preload failure and the MIME error a browser
// raises when the server answers a missing .js with index.html.
const CHUNK_ERROR_PATTERNS = [
  /Failed to fetch dynamically imported module/i,
  /error loading dynamically imported module/i,
  /Importing a module script failed/i,
  /Unable to preload CSS/i,
  /is not a valid JavaScript MIME type/i,
];

let reloadPending = false;

/** True when `error` is a failed lazy-chunk load (the stale-deploy case), not an app bug. */
export function isChunkLoadError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : typeof error === 'string' ? error : '';
  return CHUNK_ERROR_PATTERNS.some((pattern) => pattern.test(message));
}

function lastReloadAt(): number | null {
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    return raw ? Number(raw) : 0;
  } catch {
    return null; // storage blocked — can't guard against a loop, so never auto-reload
  }
}

/** Whether a stale-chunk reload is in flight or would be allowed right now. Side-effect free. */
export function canReloadForStaleChunk(now = Date.now()): boolean {
  if (reloadPending) return true;
  const last = lastReloadAt();
  return last !== null && now - last > RELOAD_GUARD_MS;
}

/**
 * Reload once to pick up the current build. Returns true if a reload is (now) in flight, false if
 * the loop guard refused — the caller should then show the error instead. Idempotent.
 */
export function reloadForStaleChunk(reload: () => void = () => window.location.reload()): boolean {
  if (reloadPending) return true;
  if (!canReloadForStaleChunk()) return false;
  try {
    window.sessionStorage.setItem(STORAGE_KEY, String(Date.now()));
  } catch {
    return false;
  }
  reloadPending = true;
  logger.warn('stale chunk — reloading to pick up the current build');
  reload();
  return true;
}

let installed = false;

/**
 * Vite dispatches `vite:preloadError` whenever a dynamic import (or its preloaded deps) fails.
 * Registered once from main.tsx. The event is NOT cancelled, so the error still reaches the route
 * errorElement, which shows an "updating" notice for the moment before the page unloads.
 */
export function installChunkReloadHandler(): void {
  if (installed || typeof window === 'undefined') return;
  installed = true;
  window.addEventListener('vite:preloadError', () => {
    reloadForStaleChunk();
  });
}

/** Test-only: clear module state between cases. */
export function resetChunkReloadForTests(): void {
  reloadPending = false;
  installed = false;
}
